import { Injectable } from "@angular/core";
import { Product, Tenor } from "shared/cubiq-model-multi-asset";
import { Rate } from "../model/Rate";

export const CURVE_SPT: string = "MARKET";
export const CURVE_FWD: string = "FWD";
export const CURVE_NDF: string = "NDF";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import LoggerService from "@bfx/logger/dist";
const logger = LoggerService.for(__dirname);

@Injectable()
export class RateCache {
  private rateMap: Object = {};
  // order id caches for detailed fill pts locking
  private fwdRateMap: Map<string, Rate> = new Map();
  private ndfRateMap: Map<string, Rate> = new Map();

  private localeString: string = "DD-MMM-YY";

  public addRates(rates: Array<Rate>): Array<Rate> {
    let changedRates: Array<Rate> = [];

    for (let i: number = 0; i < rates.length; i++) {
      let changedRate = this.addRate(rates[i], rates[i].key);
      if (changedRate) {
        changedRates.push(changedRate);
      }
      if (rates[i].horizonKey) {
        let changedRate = this.addRate(rates[i], rates[i].horizonKey);
        if (changedRate) {
          changedRates.push(changedRate);
        }
      }
    }

    return changedRates;
  }

  private addRate(newRate: Rate, key: string): Rate {
    let existingRate: Rate = this.rateMap[key];
    if (
      !existingRate ||
      existingRate.bidRate != newRate.bidRate ||
      existingRate.offerRate != newRate.offerRate ||
      existingRate.tradable != newRate.tradable ||
      existingRate.midRate != newRate.midRate
    ) {
      // logger.out.info(`Adding rate for ${key} - bid:${newRate.bidRate}, offer:${newRate.offerRate}`);
      this.rateMap[key] = newRate;
      return newRate;
    }
    return null;
  }

  public getRate(
    productCode: string,
    product: string = CURVE_SPT,
    tenor: Tenor = Tenor.SPOT,
    valueDate: Date = null,
  ): Rate {
    let rateKey = Rate.getRateKey(productCode, product, tenor, valueDate);
    return this.rateMap[rateKey];
  }

  private asDetFillRate(
    pair: CubiqTS.CurrencyPairImpl,
    order: CubiqTS.OrderDataImpl,
    value: CubiqTS.BigNumber,
    tradeable: boolean,
  ): Rate {
    let detFillRate = new Rate(pair);
    let detFillDecs = order.settleTenor === Tenor.SPOT ? detFillRate.spotDecs : detFillRate.fwdDecs;
    detFillRate.bidRate = value.toFixed(detFillDecs);
    detFillRate.offerRate = value.toFixed(detFillDecs);
    detFillRate.midRateFull = value.toNumber();
    detFillRate.midRate = value.toFixed(detFillDecs);
    detFillRate.tradable = tradeable;
    return detFillRate;
  }

  /**
   * @returns true if it had a record to clear - false if it didn't
   */
  public clearRate(
    productCode: string,
    product: string = CURVE_SPT,
    tenor: Tenor = Tenor.SPOT,
    valueDate: Date = null,
  ): boolean {
    let ret: boolean = false;
    let rateKey = Rate.getRateKey(productCode, product, tenor, valueDate);
    if (this.rateMap[rateKey]) {
      delete this.rateMap[rateKey];
      ret = true;
    }
    return ret;
  }

  public getRateByKey(rateKey: string): Rate {
    return this.rateMap[rateKey];
  }

  public hasRateByKey(rateKey: string): boolean {
    return typeof rateKey === "string" && this.rateMap[rateKey] instanceof Rate;
  }

  public getRatesByProductCode(productCode: string): Rate[] {
    return Object.keys(this.rateMap)
      .filter((key: string) => {
        return productCode === this.rateMap[key].productCode;
      })
      .map((key) => this.rateMap[key]);
  }

  public getAllRates(): Array<Rate> {
    return Object.keys(this.rateMap).map((key: string) => this.rateMap[key]);
  }

  public getSpotRateKey(order: CubiqTS.OrderDataImpl): string {
    let assetCode = order.baseCode + order.nonBaseCode;
    let curveId: string = CURVE_SPT;
    return assetCode + "-" + curveId + "-" + CubiqTS.Tenor[CubiqTS.Tenor.SPOT];
  }

  public getFwdRateKey(order: CubiqTS.OrderDataImpl): string | null {
    if (CubiqTS.Tenor.SPOT === order.settleTenor) return null;
    if (order.adjustedSettleTenor != null && CubiqTS.Tenor.SPOT === order.adjustedSettleTenor)
      return null;

    let tenor =
      order.adjustedSettleTenor != null
        ? CubiqTS.Tenor[order.adjustedSettleTenor]
        : order.settleTenor != null
          ? CubiqTS.Tenor[order.settleTenor]
          : null;
    let assetCode = order.baseCode + order.nonBaseCode;
    let settleDate: CubiqTS.Moment | null = order.settleDate;
    let valueDate: string =
      settleDate != null ? CubiqTS.moment(settleDate).utc().format(this.localeString) : null;
    let curveId: string =
      CubiqTS.Product.FWD === order.product
        ? CURVE_FWD
        : CubiqTS.Product.NDF === order.product
          ? CURVE_NDF
          : null;

    let retValue = null;
    if ((tenor === null || CubiqTS.Tenor.EMPTY === CubiqTS.Tenor[tenor]) && valueDate !== null)
      retValue = `${assetCode}-${curveId}-${valueDate}`;
    else if (tenor !== null) retValue = `${assetCode}-${curveId}-${tenor}`;

    // logger.out.info(`Forward Rate key for ${order.orderId} ='${retValue}'`)
    return retValue;
  }

  public getFwdRateNdfTrackingKey(order: CubiqTS.OrderDataImpl): string {
    // for SPT order check rate cache for both 1M, prefering FWD if available.
    // pairs are generally either FWD or NDF in practice e.g. KRW KRO
    let assetCode = order.baseCode + order.nonBaseCode;

    return `${assetCode}-${CURVE_NDF}-HORIZON`;
  }

  public setDetailFillPtsLock(pair: CubiqTS.CurrencyPairImpl, order: CubiqTS.OrderDataImpl): void {
    let fwdKey = this.getFwdRateKey(order);
    let ndfKey = this.getFwdRateNdfTrackingKey(order);

    if (fwdKey != null && order.detFillFwdPoints != null) {
      let fwdRate = this.asDetFillRate(
        pair,
        order,
        order.detFillFwdPoints,
        order.detFillTradeableFg === true,
      );
      this.fwdRateMap.set(order.orderId.toString(), fwdRate);
    }

    if (ndfKey != null && order.detFillNdfHorizonPts != null) {
      let ndfRate = this.asDetFillRate(
        pair,
        order,
        order.detFillNdfHorizonPts,
        order.detFillHorizonPtsTradeableFg === true,
      );
      this.ndfRateMap.set(order.orderId.toString(), ndfRate);
    }
  }

  public clearDetailFillPtsLock(orderId: string): void {
    this.fwdRateMap.delete(orderId);
    this.ndfRateMap.delete(orderId);
  }

  public getDetailFillFwdRate(order: any): Rate | null {
    if (order != null && CubiqTS.Long.isLong(order.orderId)) {
      let orderId = order.orderId.toString();

      return this.fwdRateMap.has(orderId) ? this.fwdRateMap.get(orderId) : null;
    }
    return null;
  }

  public getDetailFillNdfRate(order: any): Rate | null {
    if (order != null && CubiqTS.Long.isLong(order.orderId)) {
      let orderId = order.orderId.toString();

      return this.ndfRateMap.has(orderId) ? this.ndfRateMap.get(orderId) : null;
    }

    return null;
  }

  public hasDetailFillFwdRate(order: any): boolean {
    if (order != null && CubiqTS.Long.isLong(order.orderId))
      return this.fwdRateMap.has(order.orderId.toString());

    return false;
  }

  public hasDetailFillNdfRate(order: any): boolean {
    if (order != null && CubiqTS.Long.isLong(order.orderId))
      return this.ndfRateMap.has(order.orderId.toString());

    return false;
  }
}
